import dateFnsFormat from "date-fns/format"
import { IRecord, Models } from "../types/schema"

export type IEventType =
  | "note"
  | "meeting"
  | "loggedIn"
  | "create"
  | "createAssociation"
  | "letter"
  | "update"
  | "actionedStep"
  | "reminder"
  | "downloadedLetter"
  | "file"
  | "caseStateChange"
  | "message"
  | "flowResultFileRequest"
  | "completedAppraisal"
  | "scheduledAppraisal"
  | "employeeCompetencyAward"
  | "import"
  | "flowResultFileUpload"
  | "flowResultFileRequestApproval"
  | "flowResultFileApproved"
  | "flowResultFileDenied"
  | "flowResultFileRequestedChanges"
  | "emailMessage"
  | "caseFileEmailed"
  | "completedQuestionSet"

export type IEventModel =
  | "results"
  | "companies"
  | "skeem_users"
  | "teams"
  | "transcripts"
  | "files"
  | "employees"
  | "awards"

export interface IEventDetails extends Record<IEventType, unknown> {
  note: { message: string; noteType?: string; eventDetails?: any }
  completedQuestionSet: { stepId: string }
  meeting: { date: Date; notes?: string; duration?: number | null }
  loggedIn: undefined
  create: undefined
  createAssociation: { association: IEventModel; recordId: string; eventDetails?: any }
  letter: { date: Date; notes?: string }
  update: Record<string, { was: any; now: any }>
  actionedStep: { name: string; value: boolean }
  reminder: { reminderId: string; date: Date; body: string }
  downloadedLetter: {
    fileId: string
    fileName: string
    fileType: string
    date: Date
    requiresApproval?: boolean
  }
  file: {
    fileboyId: string
    flowFileId?: string
    notes?: string
    /* @deprecated */
    details?: { flowFileId?: string; notes?: string }
  }
  caseStateChange: { closed: boolean; status: string }
  message: { message: string }
  flowResultFileRequest: { flowResultFileId: string; date: Date; approverNames?: string[] }
  completedAppraisal: { date: Date; id: string }
  scheduledAppraisal: { date: Date; id: string }
  employeeCompetencyAward: { date: Date; notes?: string; competency: string }
  import: { type: string; data: Record<string, any> }
  flowResultFileUpload: {
    flowResultFileId: string
    date: Date
    fileboyId: string
    fileName: string
    userId: string
  }
  flowResultFileRequestApproval: { flowResultFileId: string; date: Date; fileName: string }
  flowResultFileApproved: { flowResultFileId: string; date: Date; fileName: string }
  flowResultFileDenied: { flowResultFileId: string; date: Date; fileName: string }
  flowResultFileRequestedChanges: { flowResultFileId: string; date: Date; fileName: string }
  emailMessage: { message: string; eventDetails?: any; assessorEmail: string }
  caseFileEmailed: { flowResultFileId: string; emailId: string }
}

export type IEventDetailsFor<T extends IEventType> = IEventDetails[T]

type ITypedEvent = {
  [key in IEventType]: {
    type: key
    details: IEventDetails[key]
    deleted: boolean
    date: Date | null
    timeTakenMinutes: number | null
  }
}[IEventType]

export type ITypedEventRecord<
  Attrs extends Exclude<keyof Models["events"], "type" | "details">,
  Type extends IEventType = IEventType,
> = IRecord<"events", Attrs> &
  ITypedEvent & {
    type: Type
  }

export const resourceIcons: Record<IEventModel, string> = {
  skeem_users: "user",
  companies: "user-friends",
  results: "award",
  teams: "object-group",
  transcripts: "file-audio",
  files: "file",
  employees: "user",
  awards: "star",
}

export const singularNames: Record<IEventModel, string> = {
  skeem_users: "user",
  companies: "company",
  results: "result",
  teams: "team",
  transcripts: "transcript",
  files: "file",
  employees: "employee",
  awards: "award",
}

export function getLinkForResource(model: IEventModel, id: string) {
  if (model === "results") {
    return `/case-coaching/${id}/results`
  }

  return null
}

export function diffRecordsForUpdateEvent(oldRecord: any, newRecord: any, attrs: string[]) {
  const diff: Record<string, { was: any; now: any }> = {}
  let isDifferent = false

  attrs.forEach(attr => {
    const was = oldRecord[attr]
    const now = newRecord[attr]

    if (was !== now && was !== undefined && now !== undefined) {
      diff[attr] = { was, now }
      isDifferent = true
    }
  })

  return { diff, isDifferent }
}

export const attributesToDisplayRecordName: Record<IEventModel, any[]> = {
  skeem_users: ["name"],
  companies: ["name"],
  results: ["flowName", "identifier"],
  teams: ["name"],
  transcripts: ["name"],
  files: ["name"],
  employees: ["firstName", "lastName"],
  awards: ["createdAt"],
}

export const formatRecordNameFromDisplayAttributes: Record<IEventModel, (record: any) => string> = {
  skeem_users: record => record.name,
  companies: record => record.name,
  results: record => `${record.flowName} ${record.identifier}`,
  teams: record => record.name,
  transcripts: record => record.name,
  files: record => record.name,
  employees: record => `${record.firstName} ${record.lastName}`,
  awards: record => dateFnsFormat(record.createdAt, "k:mm dd/MM/yyyy"),
}
